import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { discussionsAPI, libraryAPI } from '../api';
import { toast } from 'sonner';
import { ArrowLeft, MessageSquarePlus, BookOpen, Search, X, Check, FileText, ExternalLink } from 'lucide-react';
import type { SpecialtyRoom, ContextType, Article } from '../types';
import PhiBanner from '../components/PhiBanner';
import VerificationCallout from '../components/VerificationCallout';
import { useAuth } from '../contexts/AuthContext';

const NewThreadPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialSpecialty = searchParams.get('specialty');
  const initialPmid = searchParams.get('pmid');
  const initialArticleTitle = searchParams.get('articleTitle');
  const fromLitScholar = searchParams.get('from') === 'litscholar';
  const { user } = useAuth();
  const canWrite = user?.capabilities?.community_write !== false;
  
  const [title, setTitle] = useState('');
  const [specialtyId, setSpecialtyId] = useState(initialSpecialty || '');
  const [rooms, setRooms] = useState<SpecialtyRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [draftBody, setDraftBody] = useState('');
  
  // Multiple articles from state (from Library page group action)
  const [attachedArticles, setAttachedArticles] = useState<Array<{pmid: string; title: string; journal?: string}>>([]);
  
  // Library search state
  const [showLibrarySheet, setShowLibrarySheet] = useState(false);
  const [libraryArticles, setLibraryArticles] = useState<Article[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');

  // Check for attached articles from navigation state
  useEffect(() => {
    const state = location.state as { attachedArticles?: Array<{pmid: string; title: string}>; pmids?: string[] } | null;
    if (state?.attachedArticles && state.attachedArticles.length > 0) {
      setAttachedArticles(state.attachedArticles);
    } else if (initialPmid && initialArticleTitle) {
      // Single article from query params
      setAttachedArticles([{ pmid: initialPmid, title: initialArticleTitle }]);
    } else if (initialPmid) {
      setAttachedArticles([{ pmid: initialPmid, title: `PMID: ${initialPmid}` }]);
    }
  }, [location.state, initialPmid, initialArticleTitle]);

  // Read LitScholar draft from sessionStorage on mount
  useEffect(() => {
    if (fromLitScholar) {
      try {
        const raw = sessionStorage.getItem('litscholar_draft');
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft.draft_post) setDraftBody(draft.draft_post);
          if (draft.specialty_id && !initialSpecialty) setSpecialtyId(draft.specialty_id);
          sessionStorage.removeItem('litscholar_draft');
        }
      } catch { /* ignore parse errors */ }
    }
  }, [fromLitScholar, initialSpecialty]);

  useEffect(() => { loadSpecialtyRooms(); }, []);

  const loadSpecialtyRooms = async (): Promise<void> => {
    try {
      const data = await discussionsAPI.getSpecialtyRooms();
      setRooms(data.rooms);
    } catch {
      toast.error('Failed to load specialty options');
    } finally {
      setLoading(false);
    }
  };

  const loadLibraryArticles = async (): Promise<void> => {
    setLibraryLoading(true);
    try {
      const response = await libraryAPI.getLibrary();
      setLibraryArticles(response.articles || []);
    } catch {
      toast.error('Failed to load library articles');
    } finally {
      setLibraryLoading(false);
    }
  };

  const openLibrarySheet = async () => {
    setShowLibrarySheet(true);
    if (libraryArticles.length === 0) {
      await loadLibraryArticles();
    }
  };

  const toggleArticle = (article: Article) => {
    const pmid = article.pmid || article.article_id || '';
    const exists = attachedArticles.some(a => a.pmid === pmid);
    
    if (exists) {
      setAttachedArticles(prev => prev.filter(a => a.pmid !== pmid));
    } else {
      setAttachedArticles(prev => [...prev, {
        pmid,
        title: article.title,
        journal: article.journal
      }]);
    }
  };

  const removeArticle = (pmid: string) => {
    setAttachedArticles(prev => prev.filter(a => a.pmid !== pmid));
  };

  const filteredLibraryArticles = libraryArticles.filter(a =>
    !librarySearch.trim() ||
    a.title.toLowerCase().includes(librarySearch.toLowerCase()) ||
    (a.pmid && a.pmid.includes(librarySearch)) ||
    a.journal?.toLowerCase().includes(librarySearch.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!title.trim() || !specialtyId) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      // Use first attached article as primary, pass all PMIDs for reference
      const primaryPmid = attachedArticles.length > 0 ? attachedArticles[0].pmid : undefined;
      
      const thread = await discussionsAPI.createThread({
        context_type: 'specialty' as ContextType,
        context_id: specialtyId,
        specialty_id: specialtyId,
        title: title.trim(),
        // Attach primary article PMID
        primary_article_pmid: primaryPmid,
      } as any);
      toast.success('Discussion started!');
      navigate(`/community/thread/${thread.thread_id}`);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'phi_detected') {
        toast.error(detail.message || 'PHI detected. Please remove patient identifiers from the title.');
      } else if (detail?.error_code === 'community_locked') {
        toast.error('Create a digest in this specialty to join this community.');
      } else if (detail?.error_code === 'community_frozen') {
        toast.error('Your access to this community is read-only. Create a new digest to regain full access.');
      } else {
        toast.error('Failed to create discussion');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="new-thread-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/community')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Community
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquarePlus className="h-5 w-5 text-blue-600" />
              Start a New Discussion
            </CardTitle>
          </CardHeader>
          <CardContent>
            {canWrite ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Attached Articles Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-500" />
                      Attach Articles (Optional)
                    </Label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={openLibrarySheet}
                      data-testid="attach-article-btn"
                    >
                      <BookOpen className="h-4 w-4 mr-1" />
                      {attachedArticles.length > 0 ? 'Add More' : 'Select from Library'}
                    </Button>
                  </div>

                  {/* Attached Articles Display */}
                  {attachedArticles.length > 0 && (
                    <div className="space-y-2" data-testid="attached-articles-list">
                      {attachedArticles.map((article) => (
                        <div 
                          key={article.pmid}
                          className="bg-slate-50 border border-slate-200 rounded-lg p-3 relative group"
                        >
                          <button
                            type="button"
                            onClick={() => removeArticle(article.pmid)}
                            className="absolute top-2 right-2 p-1 rounded-full bg-white border border-slate-200 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:border-red-200"
                            data-testid={`remove-article-${article.pmid}`}
                          >
                            <X className="h-3 w-3 text-slate-500 hover:text-red-600" />
                          </button>
                          <div className="pr-8">
                            <p className="responsive-title text-sm font-medium text-slate-900 line-clamp-2">{article.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {article.journal && (
                                <span className="text-xs text-slate-500">{article.journal}</span>
                              )}
                              <Badge variant="secondary" className="text-[10px]">
                                PMID: {article.pmid}
                              </Badge>
                              <a
                                href={`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sky-600 hover:text-sky-700"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {attachedArticles.length === 0 && (
                    <p className="text-xs text-slate-500">
                      Attach articles from your library to give context to your discussion.
                    </p>
                  )}
                </div>

                {/* Specialty Selection */}
                <div className="space-y-2">
                  <Label htmlFor="specialty">Specialty Room *</Label>
                  <Select value={specialtyId} onValueChange={setSpecialtyId}>
                    <SelectTrigger data-testid="specialty-select">
                      <SelectValue placeholder="Select a specialty room" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map((room) => (
                        <SelectItem key={room.specialty_id} value={room.specialty_id}>
                          {room.specialty_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Discussion Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., New guidelines for heart failure management"
                    maxLength={500}
                    data-testid="thread-title-input"
                  />
                  <p className="text-xs text-gray-500">
                    {title.length}/500 characters
                  </p>
                </div>

                {/* LitScholar Draft Preview */}
                {draftBody && (
                  <div className="space-y-2" data-testid="litscholar-draft-preview">
                    <Label>Lit Scholar Draft <Badge variant="secondary" className="text-[10px] ml-1">Draft — edit before posting</Badge></Label>
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{draftBody}</p>
                    </div>
                    <p className="text-xs text-slate-500">This draft will be your first comment after creating the thread. Review and edit before posting.</p>
                  </div>
                )}

                {/* PHI-Zero Policy Banner */}
                <PhiBanner />

                {/* Submit */}
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/community')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!title.trim() || !specialtyId || submitting}
                    data-testid="create-thread-btn"
                  >
                    {submitting ? 'Creating...' : 'Start Discussion'}
                  </Button>
                </div>
              </form>
            ) : (
              <VerificationCallout />
            )}
          </CardContent>
        </Card>

        {/* Library Selection Sheet */}
        <Sheet open={showLibrarySheet} onOpenChange={setShowLibrarySheet}>
          <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
            <SheetHeader className="flex-shrink-0">
              <SheetTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-sky-600" />
                Select Articles from Library
              </SheetTitle>
            </SheetHeader>
            
            <div className="mt-4 flex flex-col flex-1 min-h-0">
              {/* Search */}
              <div className="relative flex-shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search your library..."
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  className="pl-10"
                  data-testid="library-search-input"
                />
              </div>

              {/* Selected count */}
              {attachedArticles.length > 0 && (
                <div className="mt-3 bg-sky-50 border border-sky-200 rounded-lg p-2 flex-shrink-0">
                  <p className="text-xs font-medium text-sky-700">
                    {attachedArticles.length} article{attachedArticles.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              )}

              {/* Article List */}
              <ScrollArea className="flex-1 min-h-0 mt-4">
                {libraryLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2 pr-2">
                    {filteredLibraryArticles.map((article) => {
                      const pmid = article.pmid || article.article_id || '';
                      const isSelected = attachedArticles.some(a => a.pmid === pmid);
                      return (
                        <div
                          key={pmid}
                          onClick={() => toggleArticle(article)}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-sky-400 bg-sky-50'
                              : 'border-slate-200 hover:border-sky-200 hover:bg-slate-50'
                          }`}
                          data-testid={`library-article-${pmid}`}
                        >
                          <div className="flex items-start gap-2">
                            <div
                              className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${
                                isSelected ? 'bg-sky-500 border-sky-500' : 'border-slate-300'
                              }`}
                            >
                              {isSelected && <Check className="h-3 w-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="responsive-title text-sm font-medium text-slate-900 line-clamp-2">
                                {article.title}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                {article.journal} · PMID: {pmid}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {filteredLibraryArticles.length === 0 && (
                      <div className="text-center py-8">
                        <Search className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">
                          {librarySearch ? 'No matching articles' : 'No articles in your library'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>

              {/* Done button */}
              <div className="flex-shrink-0 pt-4 border-t mt-4">
                <Button 
                  onClick={() => setShowLibrarySheet(false)}
                  className="w-full bg-sky-600 hover:bg-sky-700"
                  data-testid="library-done-btn"
                >
                  Done ({attachedArticles.length} selected)
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

export default NewThreadPage;
